/**
 * iter 73.13 — TeamLive sidebar widget.
 *
 * Sits pinned at the TOP of the sidebar and shows the caller's org-mates'
 * live driver state (status, battery). Collapsed by default: max 5 rows +
 * a "+N more" chip that expands into a scrollable list capped at ~11 rows
 * of virtual height (any more scrolls internally).
 *
 * Data sources:
 *   • Initial hydrate — `GET /api/me/sidebar/team-live` (auth'd).
 *   • Live updates — Supabase Realtime `postgres_changes` on the
 *     `public.users` table, filtered by the caller's org_id so we only
 *     see our own tenant.
 *
 * Status dots (as per spec):
 *   Active  → green   #22c55e
 *   Offline → grey    #71717a
 *   On Break → amber  #f59e0b
 *   Busy    → red     #ef4444
 *
 * A toggle in Settings → Sidebar Layout controls whether this widget
 * renders at all — see SidebarPreferencesSection.jsx (`show_team_live`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { CaretDown, CaretUp, Users } from "@phosphor-icons/react";

const STATUS_META = {
  Active:    { dot: "bg-green-500",  label: "Active"   },
  "On Break":{ dot: "bg-amber-500",  label: "On Break" },
  Busy:      { dot: "bg-red-500",    label: "Busy"     },
  Offline:   { dot: "bg-zinc-400",   label: "Offline"  },
};
const STATUS_RANK = { Active: 0, "On Break": 1, Busy: 2, Offline: 3 };
const VISIBLE_COLLAPSED = 5;   // exactly what the user asked for

function sortMembers(list) {
  // Same ordering as the backend (Active → On Break → Busy → Offline),
  // then alphabetic by name, so realtime insertions land in the right slot.
  return [...list].sort((a, b) => {
    const ra = STATUS_RANK[a.current_status || "Offline"] ?? 99;
    const rb = STATUS_RANK[b.current_status || "Offline"] ?? 99;
    if (ra !== rb) return ra - rb;
    return (a.name || "").localeCompare(b.name || "");
  });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0] || "")[0] + (parts[1] || "")[0]).toUpperCase() || "?";
}

function MemberRow({ m, compact }) {
  const meta = STATUS_META[m.current_status] || STATUS_META.Offline;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 hover:bg-muted/40 transition-colors ${compact ? "" : "cursor-default"}`}
      data-testid={`team-live-row-${m.id}`}
      title={`${m.name || "Unknown"} — ${meta.label}${m.battery_level != null ? ` · ${m.battery_level}%` : ""}`}
    >
      <div className="relative flex-shrink-0">
        {m.avatar_url ? (
          <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover bg-muted" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-muted text-[10px] font-semibold flex items-center justify-center">
            {initials(m.name)}
          </div>
        )}
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-card ${meta.dot}`}
          data-testid={`team-live-dot-${m.id}`}
          aria-label={meta.label}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs truncate leading-tight">{m.name || "Unknown"}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {meta.label}{m.battery_level != null ? ` · ${m.battery_level}%` : ""}
        </div>
      </div>
    </div>
  );
}

export default function TeamLiveWidget({ orgId, collapsed }) {
  const [members, setMembers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const channelRef = useRef(null);

  // Initial hydrate.
  const hydrate = useCallback(async () => {
    try {
      const r = await api.get("/me/sidebar/team-live");
      setMembers(Array.isArray(r.data) ? sortMembers(r.data) : []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  // Realtime subscription — one channel per org.
  useEffect(() => {
    if (!supabase || !orgId) return;
    const ch = supabase
      .channel(`team-live:${orgId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "users", filter: `org_id=eq.${orgId}` },
        (payload) => {
          const u = payload.new;
          if (!u?.id) return;
          setMembers((prev) => sortMembers(prev.map((m) =>
            m.id === u.id
              ? { ...m,
                  current_status: u.current_status || m.current_status,
                  battery_level: u.battery_level != null ? u.battery_level : m.battery_level,
                  last_location_update_at: u.last_location_update_at || m.last_location_update_at,
                }
              : m
          )));
        }
      )
      .subscribe();
    channelRef.current = ch;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [orgId]);

  if (collapsed) {
    // In a collapsed sidebar, render just a status-dot stack with counts.
    const activeCount = members.filter((m) => (m.current_status || "Offline") === "Active").length;
    return (
      <div
        className="border-b border-border py-2 flex flex-col items-center gap-1"
        data-testid="team-live-widget-collapsed"
        title={`${activeCount} active teammate${activeCount === 1 ? "" : "s"}`}
      >
        <Users size={14} className="text-muted-foreground" />
        <div className="text-[10px] font-semibold text-green-600">{activeCount}</div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="border-b border-border px-3 py-2 text-[10px] text-muted-foreground">
        Loading team…
      </div>
    );
  }
  if (members.length === 0) {
    return (
      <div className="border-b border-border px-3 py-2" data-testid="team-live-widget-empty">
        <div className="flex items-center gap-1.5 text-[10px] label-xs text-muted-foreground">
          <Users size={11} /> TEAM LIVE
        </div>
        <div className="text-[11px] text-muted-foreground italic mt-0.5">
          No teammates yet.
        </div>
      </div>
    );
  }

  const overflow = Math.max(0, members.length - VISIBLE_COLLAPSED);
  const visibleList = expanded ? members : members.slice(0, VISIBLE_COLLAPSED);

  return (
    <div
      className="border-b border-border"
      data-testid="team-live-widget"
    >
      <div className="flex items-center justify-between px-3 pt-2 pb-1">
        <div className="flex items-center gap-1.5 label-xs text-muted-foreground">
          <Users size={11} /> TEAM LIVE
        </div>
        <span className="text-[9px] mono text-muted-foreground" data-testid="team-live-total">
          {members.length}
        </span>
      </div>
      {/* Scrollable when expanded; simple stack when collapsed. */}
      <div
        className={expanded ? "overflow-y-auto max-h-[280px]" : ""}
        data-testid="team-live-list"
      >
        {visibleList.map((m) => (
          <MemberRow key={m.id} m={m} />
        ))}
      </div>
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full text-[10px] py-1.5 hover:bg-muted/40 text-muted-foreground flex items-center justify-center gap-1 transition-colors"
          data-testid="team-live-more-toggle"
          aria-expanded={expanded}
        >
          {expanded ? (
            <>Hide <CaretUp size={9} weight="bold" /></>
          ) : (
            <>+{overflow} more <CaretDown size={9} weight="bold" /></>
          )}
        </button>
      )}
    </div>
  );
}
